import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import { agGridThemes } from '../../../@shared/ag-grid-themes';

/**
 * Popup editor for Scanners.<id>.channels[] — IT-F2-406 cid 9c3a4daf.
 *
 * Scanners.<id>.channels[] drives:
 *   - Edit Channels popup on the Users panel (per-user custom:notify_channels)
 *   - SPA sidebar sub-tape list rendering
 *   - WS 4403 "channel_not_entitled" gate
 *   - REST per-channel visibility filtering
 *
 * Prior to this editor, admins had no UI to maintain this array — they
 * were editing it via raw mongo. Field set:
 *   { id, slug, title, visible_to_roles?: string[] }
 *
 * visible_to_roles semantics (per f2-session c/2d600d1a):
 *   - absent / empty → visible to all entitled
 *   - non-empty → user.custom:role must be in list
 *   - admins bypass
 *
 * Two-level modal flow (same shape as stream-editor):
 *   outer modal → AG-Grid of channels + Add / Done
 *   inner modal → single-channel form (id / slug / title / role chips)
 *     opens on row-click or Add; Delete button inside.
 */
export interface ChannelDef {
  id: string;
  slug?: string;
  title: string;
  visible_to_roles?: string[];
  // IT-F2-391 Mike clar a712d669 (2026-10-23) — "Consolidate on channels[]"
  // picked (option A). The per-channel NC settings fields that previously
  // lived on sibling Scanner.channel_templates.<id> now live inline on each
  // channels[] entry. f2-session's backend readers prefer channels[] with
  // a legacy mirror to channel_templates during the overlap window
  // (f2-admin-service PR #251). Writes go through the generic scanner PUT
  // (scanners.service.ts update_scanner forwards data.channels as an array
  // without inner validation at :457-458), so no second round-trip to the
  // NC-specific endpoint is required anymore.
  enabled?: boolean;
  body_html_template?: string;
  schedule?: {
    presets?: string[];           // subset of SCHEDULE_PRESETS
    cron?: string;                // when `custom_cron` is in presets
  };
  data_source?: {
    db_pattern?: string;          // e.g. `F2_LIVE-{yyyy-MM-dd}` — fleet-standard tier pattern
    collection?: string;          // single collection on the resolved db
    requires_schema_translation?: boolean; // __schema__ sentinel on F2 producer collections
    // IT-F2-391 Mike c/10743951 2026-10-23 — explicit per-doc date field for
    // digest-mode day-window filtering (backend f2-admin-service PR #264,
    // NC admin SPA PR #35 shipped the paired surface). When set, backend
    // filters { [date_field]: { $gte: dayStartNY, $lt: dayEndNY } } using
    // the configured field (e.g. close_date for exits, signal_entry_date
    // for entries) instead of the fallback _id ObjectId-timestamp
    // heuristic — necessary for back-fills / replays where insert time
    // ≠ semantic business day. For F2 __schema__ collections the backend
    // reverse-maps the readable name (closed_date) to the stored k-key.
    date_field?: string;
    // IT-F2-391 Mike c/daf5bc26 2026-10-23 — AND-merged into the mongo
    // find() alongside the day-window + __schema__ exclusion so the
    // email row set matches whatever filter the Scanner UI applies.
    // Example: {"signal_entry_date":"$dateVar","status":"ACTIVE"} —
    // backend substitutes $dateVar with the resolved NY-tz day's ISO
    // string. _id is reserved so the __schema__ exclusion stays intact.
    // Backend reader: f2-admin-service PR #267. Paired NC admin SPA
    // surface: notificationcenter PR #42.
    filter?: Record<string, any>;
  };
}

// Legacy name kept for consumers importing the deprecated sibling-map
// shape. Will be removed once f2-session drops the channel_templates
// mirror (post-overlap window).
/** @deprecated use ChannelDef inline NC fields; sibling map retired. */
export interface ChannelTemplateDef {
  enabled?: boolean;
  body_html_template?: string;
  schedule?: {
    presets?: string[];
    cron?: string;
  };
}

/** @deprecated use ChannelDef[]; sibling map retired. */
export type ChannelTemplatesMap = Record<string, ChannelTemplateDef>;

const ROLE_OPTIONS: ReadonlyArray<string> = ['admin', 'client_admin', 'client', 'member'];
const ROLE_LABELS: Record<string, string> = {
  admin: 'ADM',
  client_admin: 'C-ADM',
  client: 'CLI',
  member: 'MEM',
};

// IT-F2-391 clar 1bb46e0a Q1a — multi-select preset names Mike picked.
// Mapped to backend cron on send by NotificationCenter#send_alert_email
// per `custom_cron`-with-expression override. Keep ids stable (f2-session
// consumes them from Scanner.channel_templates.<id>.schedule.presets[]).
// Mike c/80cb9600 2026-10-23: hint text calls out NYSE-holiday + early-
// close awareness explicitly — the backend scheduler (f2-session item
// 8a5eb487) suppresses sends on NYSE holidays and respects early-close
// days (close 13:00 ET → digest fires 13:30 ET, not 16:30 ET).
const SCHEDULE_PRESETS: ReadonlyArray<{ id: string; label: string; hint: string }> = [
  { id: 'market_close_digest',  label: 'Market-close digest', hint: '30min after NYSE close on trading days. Honors the NYSE calendar — no sends on holidays; early-close days (e.g. day after Thanksgiving) fire 30min after the early close.' },
  { id: 'before_open_digest',   label: 'Before-open digest',  hint: '30min before NYSE open on trading days. Honors the NYSE calendar — no sends on holidays or weekends.' },
  { id: 'intraday_on_event',    label: 'Intraday on event',   hint: 'Fires as events arrive during RTH (redis channel-driven). Naturally quiet on NYSE-closed days since no events land.' },
  { id: 'custom_cron',          label: 'Custom cron',         hint: 'Provide a cron expression below; overrides the presets. Note: a bare cron does NOT skip NYSE holidays — pick a preset above if you need holiday-aware behavior.' },
];

@Component({
  standalone: true,
  selector: 'app-channels-catalog-editor',
  imports: [CommonModule, FormsModule, AgGridAngular],
  templateUrl: './channels-catalog-editor.component.html',
})
export class ChannelsCatalogEditorComponent implements OnChanges {
  @Input() channels: ChannelDef[] | null = null;
  @Input() scannerId: string = '';
  @Input() showModal: boolean = false;
  // IT-F2-391 clar a712d669 (2026-10-23) — Mike picked option A (consolidate
  // on channels[]). The sibling channel_templates @Input / @Output pair that
  // used to carry NC settings has been collapsed into inline fields on
  // ChannelDef. The props below are kept with no-op semantics so existing
  // callers passing [channelTemplates] / (channelTemplatesChange) don't
  // break during the overlap window; they are no longer written to.
  /** @deprecated kept for API compat during the overlap window — unused. */
  @Input() channelTemplates: ChannelTemplatesMap | null = null;
  // Read-only Stream reference per clar b81c62e7 Q2c pick (reuse Scanner
  // .Stream as the data-source source-of-truth; no cross-wiring from here).
  @Input() streamRef: any = null;

  @Output() channelsChange = new EventEmitter<ChannelDef[] | null>();
  /** @deprecated kept for API compat during the overlap window — never fires. */
  @Output() channelTemplatesChange = new EventEmitter<ChannelTemplatesMap | null>();
  @Output() closeModal = new EventEmitter<void>();

  readonly ROLE_OPTIONS = ROLE_OPTIONS;
  readonly ROLE_LABELS = ROLE_LABELS;
  readonly SCHEDULE_PRESETS = SCHEDULE_PRESETS;

  entries: ChannelDef[] = [];

  editingIndex: number | null = null;
  // IT-F2-391 clar a712d669 — draft channel carries the NC settings inline
  // (enabled, body_html_template, schedule, data_source). Reset to the full
  // shape on open; saved into entries[] on save.
  editingDraft: ChannelDef = { id: '', title: '' };
  // IT-F2-391 Mike c/daf5bc26 — data_source.filter in the sub-modal is
  // edited as a free-form JSON string so admins can type `$dateVar` /
  // comment / fix typos without clobbering the saved object on every
  // keystroke. Parsed once on save; invalid JSON blocks the save and
  // surfaces as a red hint under the textarea.
  editingDraftFilterJson = '';
  // IT-F2-391 Mike c/2bf04074 2026-10-23 — transient feedback flags for the
  // Copy / Paste buttons on the sub-modal action bar. Cleared on a short
  // timeout so the label flips back after the operator sees the result.
  copyBusy: '' | 'done' = '';
  pasteBusy: '' | 'done' | 'error' = '';

  gridTheme = agGridThemes.dark;
  gridOptions: any = {
    headerHeight: 30,
    rowHeight: 32,
    suppressCellFocus: true,
    rowDragManaged: true,
    animateRows: true,
    overlayNoRowsTemplate: '<span>No channels</span>',
    defaultColDef: { sortable: false, resizable: true, filter: false, suppressMovable: true },
    columnDefs: [
      { headerName: '', rowDrag: true, width: 36, maxWidth: 36, suppressSizeToFit: true, cellClass: 'cursor-grab' },
      { headerName: '#', valueGetter: (p: any) => (p.node?.rowIndex ?? 0) + 1, width: 54, maxWidth: 54, cellClass: 'font-mono text-xs' },
      { headerName: 'id', field: 'id', minWidth: 120, flex: 1, cellClass: 'font-mono text-xs' },
      { headerName: 'slug', field: 'slug', minWidth: 120, flex: 1, cellClass: 'font-mono text-xs' },
      { headerName: 'title', field: 'title', minWidth: 180, flex: 2, cellClass: 'text-xs' },
      { headerName: 'visible to roles',
        valueGetter: (p: any) => this._rolesSummary(p?.data),
        minWidth: 140, flex: 1, cellClass: 'font-mono text-xs text-amber-500' },
    ],
  };

  ngOnChanges(_: SimpleChanges) {
    this._load();
  }

  private _load() {
    const normalized: ChannelDef[] = Array.isArray(this.channels) ? [...this.channels] : [];
    // IT-F2-391 clar a712d669 — one-shot migration for any legacy
    // `channelTemplates` sibling map still passed through [channelTemplates].
    // When present, hoist each key's fields onto the matching channels[]
    // entry (keyed by channel.id === template key). Keys that don't match
    // a channel entry are silently dropped — nothing reads them anymore.
    const legacy: ChannelTemplatesMap | null = this.channelTemplates && typeof this.channelTemplates === 'object'
      ? this.channelTemplates : null;
    this.entries = normalized.map(c => {
      const copy: ChannelDef = { ...c };
      if (legacy && c.id && Object.prototype.hasOwnProperty.call(legacy, c.id)) {
        const t = legacy[c.id] || {};
        // Prefer any inline fields that already exist on the channel entry
        // (they're what Mike's keystrokes wrote this session); fill from
        // the legacy map only when the inline slot is empty.
        if (copy.enabled === undefined && t.enabled !== undefined) copy.enabled = t.enabled;
        if (!copy.body_html_template && t.body_html_template) copy.body_html_template = t.body_html_template;
        if (!copy.schedule && t.schedule && (t.schedule.presets?.length || t.schedule.cron)) {
          copy.schedule = {
            presets: Array.isArray(t.schedule.presets) ? [...t.schedule.presets] : [],
            cron: t.schedule.cron || '',
          };
        }
      }
      return copy;
    });
  }

  private _rolesSummary(c: ChannelDef | undefined): string {
    if (!c) return '';
    const roles = Array.isArray(c.visible_to_roles) ? c.visible_to_roles : [];
    if (roles.length === 0) return '(all)';
    return roles.map(r => ROLE_LABELS[r] || r).join(' · ');
  }

  // --- Outer modal: list CRUD ---

  onDone() {
    this.closeModal.emit();
  }

  onRowClicked(ev: any) {
    const i = ev?.node?.rowIndex;
    if (typeof i !== 'number' || i < 0 || i >= this.entries.length) return;
    this._openEdit(i);
  }

  openAdd() {
    this._openEdit(-1);
  }

  onRowDragEnd(_: any) {
    this._emit();
  }

  deleteFromEdit() {
    if (this.editingIndex === null) return;
    if (this.editingIndex === -1) {
      this.cancelEdit();
      return;
    }
    this.entries.splice(this.editingIndex, 1);
    this.editingIndex = null;
    this.editingDraft = { id: '', title: '' };
    this._emit();
  }

  // --- Sub-modal: single-channel form ---

  private _openEdit(index: number) {
    this.editingIndex = index;
    // IT-F2-391 clar a712d669 — NC settings live inline on the channel
    // entry; clone the full shape (incl. enabled / body_html_template /
    // schedule / data_source) so the sub-modal edits don't leak back
    // into the live entries[] array until saveEdit() flushes.
    if (index === -1) {
      this.editingDraft = {
        id: '', slug: '', title: '', visible_to_roles: [],
        enabled: false, body_html_template: '',
        schedule: { presets: [], cron: '' },
        data_source: { db_pattern: '', collection: '', requires_schema_translation: false },
      };
    } else {
      const src = this.entries[index];
      this.editingDraft = {
        ...src,
        visible_to_roles: [...(src.visible_to_roles || [])],
        enabled: !!src.enabled,
        body_html_template: src.body_html_template || '',
        schedule: {
          presets: Array.isArray(src.schedule?.presets) ? [...src.schedule!.presets!] : [],
          cron: src.schedule?.cron || '',
        },
        data_source: {
          db_pattern: src.data_source?.db_pattern || '',
          collection: src.data_source?.collection || '',
          requires_schema_translation: !!src.data_source?.requires_schema_translation,
          date_field: src.data_source?.date_field || '',
          filter: src.data_source?.filter && typeof src.data_source.filter === 'object'
            ? { ...src.data_source.filter } : undefined,
        },
      };
    }
    // Rehydrate the free-form JSON textarea from the stored object.
    const f = this.editingDraft.data_source?.filter;
    this.editingDraftFilterJson = (f && typeof f === 'object' && Object.keys(f).length)
      ? JSON.stringify(f, null, 2) : '';
  }

  cancelEdit() {
    this.editingIndex = null;
    this.editingDraft = { id: '', title: '' };
    this.editingDraftFilterJson = '';
    this.copyBusy = '';
    this.pasteBusy = '';
  }

  // IT-F2-391 Mike c/2bf04074 — ship the current draft's non-identity
  // settings (NC fields + roles + the raw filter JSON string so round-
  // tripping doesn't lose comments / spacing) to the clipboard as JSON.
  // id / slug / title stay out of the payload so pasting onto another
  // channel doesn't collide the identity.
  async copyDraftSettings() {
    const payload: Record<string, any> = {
      visible_to_roles: Array.isArray(this.editingDraft.visible_to_roles)
        ? [...this.editingDraft.visible_to_roles].sort() : [],
      enabled: !!this.editingDraft.enabled,
      body_html_template: this.editingDraft.body_html_template || '',
      schedule: {
        presets: Array.isArray(this.editingDraft.schedule?.presets)
          ? [...this.editingDraft.schedule!.presets!] : [],
        cron: this.editingDraft.schedule?.cron || '',
      },
      data_source: {
        db_pattern: this.editingDraft.data_source?.db_pattern || '',
        collection: this.editingDraft.data_source?.collection || '',
        requires_schema_translation: !!this.editingDraft.data_source?.requires_schema_translation,
        date_field: this.editingDraft.data_source?.date_field || '',
        filter_json: this.editingDraftFilterJson || '',
      },
    };
    const serialized = JSON.stringify(payload, null, 2);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(serialized);
      } else {
        // Fallback for insecure contexts (should be rare on admin.f2-tech.ai).
        const ta = document.createElement('textarea');
        ta.value = serialized;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      this.copyBusy = 'done';
      setTimeout(() => { if (this.copyBusy === 'done') this.copyBusy = ''; }, 1200);
    } catch {
      // No visual error for Copy — the fallback almost always works; a
      // clipboard-permission denial is rare enough that a terse re-click
      // affordance is acceptable.
      this.copyBusy = '';
    }
  }

  // IT-F2-391 Mike c/2bf04074 — overlay settings from the clipboard JSON
  // onto the current draft. Keeps id / slug / title so the channel's
  // identity doesn't get clobbered when pasting a template from another
  // scanner. Invalid JSON surfaces as a "Bad JSON" label on the button;
  // save-flow unchanged (saveEdit() still validates filter JSON etc).
  async pasteDraftSettings() {
    let raw = '';
    try {
      raw = navigator?.clipboard?.readText
        ? await navigator.clipboard.readText()
        : '';
    } catch {
      raw = '';
    }
    if (!raw) {
      this.pasteBusy = 'error';
      setTimeout(() => { if (this.pasteBusy === 'error') this.pasteBusy = ''; }, 1500);
      return;
    }
    let parsed: any;
    try { parsed = JSON.parse(raw); } catch {
      this.pasteBusy = 'error';
      setTimeout(() => { if (this.pasteBusy === 'error') this.pasteBusy = ''; }, 1500);
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      this.pasteBusy = 'error';
      setTimeout(() => { if (this.pasteBusy === 'error') this.pasteBusy = ''; }, 1500);
      return;
    }
    // Overlay — don't blow away identity fields. Only overwrite slots that
    // are explicitly present on the pasted payload so partial copies work.
    if (Array.isArray(parsed.visible_to_roles)) {
      this.editingDraft.visible_to_roles = parsed.visible_to_roles.map((x: any) => String(x || '').trim()).filter(Boolean);
    }
    if (parsed.enabled !== undefined) this.editingDraft.enabled = !!parsed.enabled;
    if (typeof parsed.body_html_template === 'string') {
      this.editingDraft.body_html_template = parsed.body_html_template;
    }
    if (parsed.schedule && typeof parsed.schedule === 'object') {
      this.editingDraft.schedule = this.editingDraft.schedule || { presets: [], cron: '' };
      if (Array.isArray(parsed.schedule.presets)) this.editingDraft.schedule.presets = [...parsed.schedule.presets];
      if (typeof parsed.schedule.cron === 'string') this.editingDraft.schedule.cron = parsed.schedule.cron;
    }
    if (parsed.data_source && typeof parsed.data_source === 'object') {
      this.editingDraft.data_source = this.editingDraft.data_source || {};
      const ds = parsed.data_source;
      if (typeof ds.db_pattern === 'string') this.editingDraft.data_source.db_pattern = ds.db_pattern;
      if (typeof ds.collection === 'string') this.editingDraft.data_source.collection = ds.collection;
      if (ds.requires_schema_translation !== undefined) this.editingDraft.data_source.requires_schema_translation = !!ds.requires_schema_translation;
      if (typeof ds.date_field === 'string') this.editingDraft.data_source.date_field = ds.date_field;
      // The copy payload intentionally ships filter_json so the admin keeps
      // their formatting; prefer that over a pre-parsed `filter` object
      // when both are present.
      if (typeof ds.filter_json === 'string') {
        this.editingDraftFilterJson = ds.filter_json;
      } else if (ds.filter && typeof ds.filter === 'object' && !Array.isArray(ds.filter)) {
        this.editingDraftFilterJson = JSON.stringify(ds.filter, null, 2);
      }
    }
    this.pasteBusy = 'done';
    setTimeout(() => { if (this.pasteBusy === 'done') this.pasteBusy = ''; }, 1200);
  }

  // IT-F2-391 Mike c/daf5bc26 — JSON-parse the row-filter textarea once
  // on save. Blank → undefined (dropped on round-trip). Returns '' on
  // success + populates editingDraft.data_source.filter; returns a short
  // error string that the sub-modal surfaces inline when invalid.
  draftFilterError(): string {
    const raw = (this.editingDraftFilterJson || '').trim();
    if (!raw) return '';
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return 'must be a JSON object (e.g. {"status":"ACTIVE"})';
      }
    } catch (e: any) {
      return 'invalid JSON: ' + (e?.message || 'parse error');
    }
    return '';
  }

  saveEdit() {
    if (this.editingIndex === null) return;
    if (this.draftIdError()) return;
    if (this.draftTitleError()) return;
    if (this.draftFilterError()) return;
    const clean: ChannelDef = {
      id: (this.editingDraft.id || '').trim(),
      title: (this.editingDraft.title || '').trim(),
    };
    const slug = (this.editingDraft.slug || '').trim();
    if (slug) clean.slug = slug;
    const roles = Array.isArray(this.editingDraft.visible_to_roles)
      ? this.editingDraft.visible_to_roles.slice().sort() : [];
    if (roles.length > 0) clean.visible_to_roles = roles;
    // IT-F2-391 clar a712d669 — fold NC settings onto the channel entry.
    // Only emit a field when the admin set a non-default value so the
    // round-tripped channels[] stays tidy (no empty strings / empty maps).
    if (this.editingDraft.enabled) clean.enabled = true;
    const bodyTpl = (this.editingDraft.body_html_template || '').trim();
    if (bodyTpl) clean.body_html_template = bodyTpl;
    const presets = Array.isArray(this.editingDraft.schedule?.presets)
      ? this.editingDraft.schedule!.presets!.slice().sort() : [];
    const cron = (this.editingDraft.schedule?.cron || '').trim();
    if (presets.length > 0 || cron) {
      clean.schedule = {};
      if (presets.length > 0) clean.schedule.presets = presets;
      if (presets.includes('custom_cron') && cron) clean.schedule.cron = cron;
    }
    const dbPat = (this.editingDraft.data_source?.db_pattern || '').trim();
    const coll = (this.editingDraft.data_source?.collection || '').trim();
    const reqX = !!this.editingDraft.data_source?.requires_schema_translation;
    const dateField = (this.editingDraft.data_source?.date_field || '').trim();
    const rawFilter = (this.editingDraftFilterJson || '').trim();
    let parsedFilter: Record<string, any> | undefined;
    if (rawFilter) {
      try {
        const p = JSON.parse(rawFilter);
        if (p && typeof p === 'object' && !Array.isArray(p) && Object.keys(p).length) {
          parsedFilter = p;
        }
      } catch { /* caught by draftFilterError() */ }
    }
    if (dbPat || coll || reqX || dateField || parsedFilter) {
      clean.data_source = {};
      if (dbPat) clean.data_source.db_pattern = dbPat;
      if (coll) clean.data_source.collection = coll;
      if (reqX) clean.data_source.requires_schema_translation = true;
      if (dateField) clean.data_source.date_field = dateField;
      if (parsedFilter) clean.data_source.filter = parsedFilter;
    }
    if (this.editingIndex === -1) {
      this.entries.push(clean);
    } else {
      this.entries[this.editingIndex] = clean;
    }
    this.editingIndex = null;
    this.editingDraft = { id: '', title: '' };
    this.editingDraftFilterJson = '';
    this._emit();
  }

  // IT-F2-391 clar a712d669 — sub-modal controls for schedule.presets[]
  // on the draft channel entry.

  isDraftPresetSelected(id: string): boolean {
    const arr = this.editingDraft.schedule?.presets || [];
    return arr.includes(id);
  }

  toggleDraftPreset(id: string) {
    const set = new Set<string>(this.editingDraft.schedule?.presets || []);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    this.editingDraft.schedule = this.editingDraft.schedule || { presets: [], cron: '' };
    this.editingDraft.schedule.presets = Array.from(set);
  }

  get draftCustomCronEnabled(): boolean {
    return !!(this.editingDraft.schedule?.presets || []).includes('custom_cron');
  }

  // --- Role chip controls on the sub-modal draft ---

  isDraftRoleSelected(role: string): boolean {
    const roles = this.editingDraft.visible_to_roles || [];
    return roles.includes(role);
  }

  toggleDraftRole(role: string) {
    const roles = new Set<string>(this.editingDraft.visible_to_roles || []);
    if (roles.has(role)) roles.delete(role);
    else roles.add(role);
    this.editingDraft.visible_to_roles = Array.from(roles);
  }

  // --- Validation ---

  draftIdError(): string {
    const v = (this.editingDraft.id || '').trim();
    if (!v) return 'required';
    for (let i = 0; i < this.entries.length; i++) {
      if (i === this.editingIndex) continue;
      if ((this.entries[i].id || '').trim() === v) return 'duplicate id';
    }
    return '';
  }

  draftTitleError(): string {
    return (this.editingDraft.title || '').trim() ? '' : 'required';
  }

  canSaveDraft(): boolean {
    return !this.draftIdError() && !this.draftTitleError();
  }

  // --- Shared ---

  private _emit() {
    const out = this.entries
      .map(e => {
        const c: ChannelDef = { id: String(e.id || '').trim(), title: String(e.title || '').trim() };
        const s = String(e.slug || '').trim();
        if (s) c.slug = s;
        const r = Array.isArray(e.visible_to_roles) ? e.visible_to_roles.slice().sort() : [];
        if (r.length > 0) c.visible_to_roles = r;
        // IT-F2-391 clar a712d669 — pass through inline NC fields so
        // they land on the scanner doc in the same channels[] write.
        if (e.enabled) c.enabled = true;
        const bodyTpl = String(e.body_html_template || '').trim();
        if (bodyTpl) c.body_html_template = bodyTpl;
        const presets = Array.isArray(e.schedule?.presets)
          ? e.schedule!.presets!.slice().sort().filter(Boolean) : [];
        const cron = String(e.schedule?.cron || '').trim();
        if (presets.length || cron) {
          c.schedule = {};
          if (presets.length) c.schedule.presets = presets;
          if (presets.includes('custom_cron') && cron) c.schedule.cron = cron;
        }
        const dbPat = String(e.data_source?.db_pattern || '').trim();
        const coll = String(e.data_source?.collection || '').trim();
        const reqX = !!e.data_source?.requires_schema_translation;
        const dateField = String(e.data_source?.date_field || '').trim();
        const flt = e.data_source?.filter;
        const fltClean = (flt && typeof flt === 'object' && !Array.isArray(flt) && Object.keys(flt).length)
          ? flt : null;
        if (dbPat || coll || reqX || dateField || fltClean) {
          c.data_source = {};
          if (dbPat) c.data_source.db_pattern = dbPat;
          if (coll) c.data_source.collection = coll;
          if (reqX) c.data_source.requires_schema_translation = true;
          if (dateField) c.data_source.date_field = dateField;
          if (fltClean) c.data_source.filter = fltClean;
        }
        return c;
      })
      .filter(c => c.id && c.title);
    this.channelsChange.emit(out.length ? out : null);
  }

  // IT-F2-391 cid 52e3671a — read-only Stream snapshot for the sub-modal.
  // Picks the first Stream entry (fleet default when a scanner has only
  // one feed); multi-feed scanners show the first and a count hint.
  get streamSnapshot(): { source_db_pattern?: string; collection?: string; key_field?: string; count: number } {
    const s = this.streamRef;
    const arr = Array.isArray(s) ? s : (s && typeof s === 'object' ? [s] : []);
    const first = arr[0] || {};
    return {
      source_db_pattern: first?.SourceDbPattern || first?.source_db_pattern || '',
      collection: first?.Collection || first?.collection || '',
      key_field: first?.KeyField || first?.key_field || '',
      count: arr.length,
    };
  }

  onBackdropClick(ev: MouseEvent, source: 'outer' | 'inner') {
    ev.stopPropagation();
    if (ev.target !== ev.currentTarget) return;
    if (source === 'inner') this.cancelEdit();
    else this.closeModal.emit();
  }
}
